import { useEffect, useRef } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/** Busy-state label shown on the confirm button while a confirm is in flight. */
export const CONFIRM_DIALOG_BUSY_LABEL = 'Working…';

type Props = {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Danger styling (red) on the confirm action + warning icon. */
  isDestructive?: boolean;
  /** Disables cancel/Escape/backdrop and shows the busy label while a confirm is in flight. */
  isConfirming?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Shared confirmation dialog for destructive or irreversible actions
 * (rule delete, appointment cancel). Tailwind-only; alertdialog semantics with
 * labelled title/description, Escape + backdrop dismissal (both suppressed
 * while confirming), and confirm auto-focus on open.
 */
export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isDestructive = false,
  isConfirming = false,
  onConfirm,
  onCancel,
}: Props) {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the confirm action whenever the dialog opens.
  useEffect(() => {
    if (isOpen) confirmButtonRef.current?.focus();
  }, [isOpen]);

  // Escape dismisses — never while a confirm is in flight.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !isConfirming) onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, isConfirming, onCancel]);

  if (!isOpen) return null;

  const handleBackdropClick = (): void => {
    if (!isConfirming) onCancel();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        data-testid="confirm-dialog-backdrop"
        aria-hidden="true"
        onClick={handleBackdropClick}
        className="absolute inset-0 bg-black/40"
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
        className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
      >
        <div className="flex items-start gap-3">
          {isDestructive && (
            <ExclamationTriangleIcon
              className="h-6 w-6 shrink-0 text-red-600"
              aria-hidden="true"
            />
          )}
          <div className="min-w-0 flex-1">
            <h2
              id="confirm-dialog-title"
              className="text-base font-semibold text-gray-900"
            >
              {title}
            </h2>
            <p
              id="confirm-dialog-message"
              className="mt-2 text-sm text-gray-600"
            >
              {message}
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isConfirming}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            disabled={isConfirming}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${
              isDestructive
                ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500'
                : 'bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500'
            }`}
          >
            {isConfirming ? CONFIRM_DIALOG_BUSY_LABEL : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
